import logger from '@eneco/common/log';

export type CmsLogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/** One log record from the CMS layer. `uuid` is a stable six-character id for the call site. */
export interface CmsLogEntry {
  level: CmsLogLevel;
  uuid: string;
  message: string;
  error?: Error;
  context?: Record<string, unknown>;
}

export interface CmsLogSink {
  /** `trace` to `warn`, such as a block with no registered component or a provider fetch that failed. */
  trace?: (entry: CmsLogEntry) => void;
  /** `error` and `fatal`, such as a component that threw while rendering. */
  exception?: (entry: CmsLogEntry) => void;
}

const toEntry = ({
  level,
  uuid,
  message,
  error,
  context,
}: Parameters<typeof logger.loggerTraceFn>[0]): CmsLogEntry => ({
  level,
  uuid,
  message,
  error,
  context: context as Record<string, unknown> | undefined,
});

/**
 * Sends the CMS layer's logs to the host's own monitoring. Call it once, at startup, on the server
 * and in the browser. Without it these logs only reach the console.
 *
 * Not for containers in this repo: there `@eneco/common/application-insights` connects the same
 * logger to Application Insights, and calling this would replace that connection for the whole app.
 */
export const setCmsLogSink = ({ trace, exception }: CmsLogSink): void => {
  if (trace) logger.setLoggerTraceFn(entry => trace(toEntry(entry)));
  if (exception) logger.setLoggerExceptionFn(entry => exception(toEntry(entry)));
};
